// Utilitário para detectar e gerenciar arquivos em textos de prompt

export interface DetectedFile {
  originalUrl: string;
  startIndex: number;
  endIndex: number;
  isFromCDN: boolean;
  fileName?: string;
  fileType?: 'image' | 'video' | 'document';
}

export interface FileReplacement {
  originalUrl: string;
  newUrl: string;
  startIndex: number;
  endIndex: number;
}

/**
 * Detecta URLs de arquivos no texto do prompt
 */
export function detectFilesInText(text: string): DetectedFile[] {
  const files: DetectedFile[] = [];
  
  // Regex para detectar URLs de arquivos (imagens, vídeos, documentos)
  const fileUrlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+\.(jpg|jpeg|png|gif|bmp|webp|svg|mp4|avi|mov|wmv|flv|webm|mkv|pdf|doc|docx|txt|rtf|xls|xlsx|ppt|pptx|zip|rar|7z))/gi;
  
  let match;
  while ((match = fileUrlRegex.exec(text)) !== null) {
    const url = match[0];
    const startIndex = match.index;
    const endIndex = startIndex + url.length;
    const extension = match[2].toLowerCase();
    
    // Determinar tipo do arquivo
    let fileType: 'image' | 'video' | 'document';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
      fileType = 'image';
    } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
      fileType = 'video';
    } else {
      fileType = 'document';
    }
    
    // Verificar se é do CDN
    const isFromCDN = url.includes('gaia-plus.nyc3.digitaloceanspaces.com');
    
    // Extrair nome do arquivo da URL
    const fileName = url.split('/').pop() || '';
    
    files.push({
      originalUrl: url,
      startIndex,
      endIndex,
      isFromCDN,
      fileName,
      fileType
    });
  }
  
  return files;
}

/**
 * Substitui uma URL de arquivo no texto
 */
export function replaceFileInText(text: string, replacement: FileReplacement): string {
  const before = text.substring(0, replacement.startIndex);
  const after = text.substring(replacement.endIndex);
  return before + replacement.newUrl + after;
}

/**
 * Adiciona um arquivo ao final do texto
 */
export function addFileToText(text: string, fileUrl: string, fileType: 'image' | 'video' | 'document'): string {
  const trimmedText = text.trim();
  const separator = trimmedText ? '\n\n' : '';
  
  // Adicionar o arquivo com formatação apropriada
  if (fileType === 'image') {
    return trimmedText + separator + `![Imagem](${fileUrl})`;
  } else if (fileType === 'video') {
    return trimmedText + separator + `[Vídeo](${fileUrl})`;
  } else {
    return trimmedText + separator + `[Documento](${fileUrl})`;
  }
}

/**
 * Remove um arquivo do texto
 */
export function removeFileFromText(text: string, fileUrl: string): string {
  // Remove a URL e qualquer formatação markdown associada
  const markdownPattern = new RegExp(`!?\\[.*?\\]\\(${fileUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)`, 'g');
  const directUrlPattern = new RegExp(fileUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
  
  let result = text.replace(markdownPattern, '');
  result = result.replace(directUrlPattern, '');
  
  // Limpar espaços duplos e quebras de linha extras
  result = result.replace(/\n\s*\n\s*\n/g, '\n\n');
  result = result.trim();
  
  return result;
}

/**
 * Valida se uma URL é de um arquivo válido
 */
export function isValidFileUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();
    
    const validExtensions = [
      '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg',
      '.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv',
      '.pdf', '.doc', '.docx', '.txt', '.rtf', '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.rar', '.7z'
    ];
    
    return validExtensions.some(ext => pathname.endsWith(ext));
  } catch {
    return false;
  }
}
